import { useEffect, useRef, useState } from 'react';
import { supabaseClient } from '../../config/supabase';

export default function useViewModePreference({ userId, storageKey, modes, defaultMode }) {
    const [viewMode, setViewMode] = useState(() => {
        try {
            const saved = localStorage.getItem(storageKey);
            return modes.includes(saved) ? saved : defaultMode;
        } catch {
            return defaultMode;
        }
    });
    const viewModeReadyRef = useRef(false);
    const skipNextPersistRef = useRef(false);

    useEffect(() => {
        let alive = true;
        // UX pass 2026-10-25: a new sign-in must READ the saved choice before anything is written, or the default would
        // overwrite it. (This effect runs before the save effect below in the same render.)
        viewModeReadyRef.current = false;
        if (!userId) {
            viewModeReadyRef.current = true;
            return () => { alive = false; };
        }
        (async () => {
            const { data, error } = await supabaseClient
                .from('user_preferences')
                .select('value')
                .eq('user_id', userId)
                .eq('key', storageKey)
                .maybeSingle();
            if (!alive) return;
            if (!error && modes.includes(data?.value)) {
                setViewMode((previous) => {
                    if (previous === data.value) return previous;
                    skipNextPersistRef.current = true;
                    return data.value;
                });
            }
            viewModeReadyRef.current = true;
        })();
        return () => { alive = false; };
    }, [userId, storageKey, modes]);

    useEffect(() => {
        try {
            localStorage.setItem(storageKey, viewMode);
        } catch {
            // Private browsing can disable local storage; the server preference still works.
        }
        if (!userId || !viewModeReadyRef.current) return;
        if (skipNextPersistRef.current) {
            skipNextPersistRef.current = false;
            return;
        }
        supabaseClient
            .from('user_preferences')
            .upsert(
                { user_id: userId, key: storageKey, value: viewMode, updated_at: new Date().toISOString() },
                { onConflict: 'user_id,key' }
            )
            .then(({ error }) => {
                if (error) console.error('Error saving view mode preference:', error);
            });
    }, [viewMode, userId, storageKey]);

    return [viewMode, setViewMode];
}
